/**
 * 手机国际区号与号码校验（注册/登录等按需引用）。
 * 新增区号：在本数组追加一项即可。
 */
export interface PhoneDialCodeConfig {
  /** E.164 国家码前缀，如 +86 */
  dialCode: string
  /** 区号选择器展示文案，默认同 dialCode */
  label?: string
  /** 校验用户输入的「国内号码」去掉非数字后的形态（不含国家码） */
  nationalDigitsPattern: RegExp
  /** pattern 未通过时 i18n key，缺省为 0db00d4c */
  invalidMessageLngKey?: string
}

const digitsOnly = (raw: string) => raw.replace(/\D/g, '')

/** 以下区号国内号段统一 6–15 位数字 */
const NATIONAL_DIGITS_6_15 = /^\d{6,15}$/

const PHONE_DIAL_CODE_EXTRA: readonly string[] = [
  '+7',
  '+20',
  '+27',
  '+30',
  '+31',
  '+32',
  '+33',
  '+34',
  '+39',
  '+40',
  '+45',
  '+46',
  '+47',
  '+48',
  '+49',
  '+51',
  '+52',
  '+54',
  '+55',
  '+60',
  '+62',
  '+63',
  '+64',
  '+65',
  '+66',
  '+84',
  '+90',
  '+91',
  '+92',
  '+95',
  '+98',
  '+211',
  '+212',
  '+213',
  '+351',
  '+352',
  '+355',
  '+380',
  '+420',
  '+421',
  '+423',
  '+855',
  '+880',
  '+960',
  '+966',
  '+971',
  '+972',
] as const

export const PHONE_DIAL_CODES: readonly PhoneDialCodeConfig[] = [
  { dialCode: '+86', nationalDigitsPattern: /^\d{11}$/ },
  { dialCode: '+81', nationalDigitsPattern: /^\d{10,11}$/ },
  { dialCode: '+82', nationalDigitsPattern: /^\d{9,11}$/ },
  { dialCode: '+852', nationalDigitsPattern: /^\d{8}$/ },
  { dialCode: '+853', nationalDigitsPattern: /^\d{7,8}$/ },
  { dialCode: '+886', nationalDigitsPattern: /^\d{8,10}$/ },
  { dialCode: '+61', nationalDigitsPattern: /^\d{9,10}$/ },
  { dialCode: '+44', nationalDigitsPattern: /^\d{10,11}$/ },
  { dialCode: '+1', nationalDigitsPattern: /^\d{10}$/ },
  ...PHONE_DIAL_CODE_EXTRA.map(
    (dialCode): PhoneDialCodeConfig => ({
      dialCode,
      nationalDigitsPattern: NATIONAL_DIGITS_6_15,
    }),
  ),
] as const

export const DEFAULT_PHONE_DIAL_CODE = PHONE_DIAL_CODES[0].dialCode

export function getPhoneDialConfig(dialCode?: string): PhoneDialCodeConfig {
  return PHONE_DIAL_CODES.find((c) => c.dialCode === dialCode) ?? PHONE_DIAL_CODES[0]
}

/** 区号下拉 options */
export function getPhoneDialSelectOptions() {
  return PHONE_DIAL_CODES.map((c) => ({
    value: c.dialCode,
    label: c.label ?? c.dialCode,
  }))
}

/** 国内号码去非数字，作为接口 account（与 countryRegionCode 分字段传递） */
export function nationalDigitsOnly(nationalRaw: string): string {
  return digitsOnly(nationalRaw)
}

/** @deprecated 旧接口区号拼在 account 前时使用；新接口请用 nationalDigitsOnly + countryRegionCode */
export function formatPhoneAccount(dialCode: string | undefined, nationalRaw: string): string {
  const cfg = getPhoneDialConfig(dialCode)
  return `${cfg.dialCode}${digitsOnly(nationalRaw)}`
}

/** 用于表单 validator：去非数字后是否满足当前区号规则 */
export function isNationalNumberValid(dialCode: string | undefined, nationalRaw: string): boolean {
  const cfg = getPhoneDialConfig(dialCode)
  const compact = digitsOnly(nationalRaw)
  return cfg.nationalDigitsPattern.test(compact)
}
